import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import Chart from '../../components/ui/Chart';
import { Users, Server, AlertTriangle, Settings, Activity, Building, PieChart, TrendingUp } from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { getMockData } = useDataContext();
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [systemHealthTrends, setSystemHealthTrends] = useState([]);

  useEffect(() => {
    // Load static data
    const dashboardData = getMockData('superAdminDashboard');
    const tenantsData = getMockData('tenants');
    const activityData = getMockData('recentActivity');
    
    setMetrics(dashboardData.metrics);
    setTenants(tenantsData.tenants || []);
    setRecentActivity(activityData.activities || []);
    
    // Load system health trends
    if (dashboardData.systemHealth?.trends) {
      setSystemHealthTrends(dashboardData.systemHealth.trends);
    } else {
      // Generate default trends data (last 7 days)
      const defaultTrends = [];
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        defaultTrends.push({
          name: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          value: 99.5 + Math.random() * 0.5 // Random between 99.5 and 100
        });
      }
      setSystemHealthTrends(defaultTrends);
    }
  }, [getMockData]);

  // Calculate tenant status distribution
  const tenantStatusData = useMemo(() => {
    const statusCounts = {
      active: 0,
      suspended: 0,
      pending: 0,
      inactive: 0
    };

    tenants.forEach(tenant => {
      const status = tenant.status?.toLowerCase() || 'inactive';
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      } else {
        statusCounts.inactive++;
      }
    });

    return [
      { name: 'Active', value: statusCounts.active, color: '#10B981' },
      { name: 'Suspended', value: statusCounts.suspended, color: '#EF4444' },
      { name: 'Pending', value: statusCounts.pending, color: '#F59E0B' },
      { name: 'Inactive', value: statusCounts.inactive, color: '#6B7280' }
    ].filter(item => item.value > 0); // Only show statuses with values
  }, [tenants]);

  return (
    <div className="w-full" style={{ backgroundColor: '#F8FCFB', margin: 0, padding: 0 }}>
      {/* Hero Section with Background */}
      <section className="relative py-6 sm:py-8 px-4 sm:px-6 lg:px-8 overflow-hidden" style={{ margin: 0 }}>
        {/* Muted background overlay */}
        <div className="absolute inset-0" style={{ backgroundColor: '#F8FCFB' }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-2 sm:mb-3" style={{ backgroundColor: 'rgba(0, 102, 0, 0.10)', border: '1px solid rgba(0, 102, 0, 0.20)' }}>
              <span className="text-xs sm:text-sm font-semibold" style={{ color: '#006600' }}>Enterprise Platform Management</span>
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: '#0B2530', margin: 0 }}>
              Super Admin Dashboard
            </h1>
            <p className="text-sm sm:text-base lg:text-lg max-w-3xl mx-auto text-center" style={{ color: '#4A5B63', margin: '0 auto', padding: 0 }}>
              Complete control over platform, tenants, and system operations
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <div className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8 relative" style={{ backgroundColor: '#F8FCFB', margin: 0, paddingTop: '1.25rem' }}>
        <div className="space-y-6">

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Tenants</p>
                    <p className="text-2xl font-bold text-foreground">{metrics?.totalTenants || 0}</p>
                  </div>
                  <Users className="h-8 w-8" style={{ color: '#006600' }} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Tenants</p>
                    <p className="text-2xl font-bold text-foreground">{metrics?.activeTenants || 0}</p>
                  </div>
                  <Server className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">System Alerts</p>
                    <p className="text-2xl font-bold text-foreground">{metrics?.systemAlerts || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8" style={{ color: '#BB0000' }} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">System Health</p>
                    <p className="text-2xl font-bold text-foreground">{metrics?.systemHealth || '99.9%'}</p>
                  </div>
                  <Settings className="h-8 w-8" style={{ color: '#0B2530' }} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tenants Status Distribution */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <PieChart className="h-5 w-5" style={{ color: '#006600' }} />
                  <span>Tenants Status Distribution</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tenantStatusData.length > 0 ? (
                  <div className="space-y-4">
                    <Chart 
                      type="pie" 
                      data={tenantStatusData} 
                      height={300}
                      colors={tenantStatusData.map(item => item.color)}
                    />
                    <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
                      {tenantStatusData.map((item) => (
                        <div key={item.name} className="flex items-center space-x-2">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: item.color }}
                          />
                          <div>
                            <p className="text-sm font-medium text-foreground">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.value} tenants</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <PieChart className="h-12 w-12 mx-auto mb-2" style={{ color: '#94A3AB' }} />
                    <p className="text-muted-foreground">No tenant data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* System Health Trends */}
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5" style={{ color: '#006600' }} />
                  <span>System Health Trends</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {systemHealthTrends.length > 0 ? (
                  <div className="space-y-4">
                    <Chart 
                      type="line" 
                      data={systemHealthTrends} 
                      height={300}
                      colors={['#10B981']}
                      dataKeys={[{ dataKey: 'value', name: 'Health %' }]}
                      options={{ legend: false }}
                    />
                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div>
                        <p className="text-sm text-muted-foreground">Average Health</p>
                        <p className="text-lg font-bold text-foreground">
                          {systemHealthTrends.length > 0 
                            ? (systemHealthTrends.reduce((sum, item) => sum + item.value, 0) / systemHealthTrends.length).toFixed(2)
                            : '0.00'}%
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Current Status</p>
                        <p className="text-lg font-bold" style={{ color: '#006600' }}>
                          {systemHealthTrends.length > 0 
                            ? systemHealthTrends[systemHealthTrends.length - 1].value.toFixed(2)
                            : '0.00'}%
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <TrendingUp className="h-12 w-12 mx-auto mb-2" style={{ color: '#94A3AB' }} />
                    <p className="text-muted-foreground">No health data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Tenants Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building className="h-5 w-5" />
                  <span>Recent Tenants</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(tenants || []).slice(0, 5).map((tenant) => (
                    <div key={tenant.id} className="border border-border rounded-lg p-4 hover:bg-[#F8FCFB] transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm text-white"
                            style={{
                              backgroundColor: tenant.status === 'active' ? '#10B981' :
                                              tenant.status === 'suspended' ? '#EF4444' : '#F59E0B'
                            }}>
                            {tenant.name?.charAt(0) || 'T'}
                          </div>
                          <div>
                            <h3 className="font-medium text-foreground">
                              {tenant.name || 'Unknown Tenant'}
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              {tenant.domain || 'No domain'}
                            </p>
                          </div>
                        </div>
                        <Badge variant={tenant.status === 'active' ? 'default' : tenant.status === 'suspended' ? 'destructive' : 'warning'}>
                          {tenant.status || 'unknown'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {tenant.userCount || 0} users • Last active: {tenant.lastActivity ? new Date(tenant.lastActivity).toLocaleDateString() : 'N/A'}
                        </span>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin/organization')}>
                          Manage
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!tenants || tenants.length === 0) && (
                    <div className="text-center py-8">
                      <Building className="h-12 w-12 mx-auto mb-2" style={{ color: '#94A3AB' }} />
                      <p className="text-muted-foreground">No tenants found</p>
                    </div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-border text-center">
                  <Button onClick={() => navigate('/super-admin/organization')}>
                    View All Tenants
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Activity className="h-5 w-5" />
                  <span>Recent Activity</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(recentActivity || []).slice(0, 5).map((activity, index) => (
                    <div key={index} className="border border-border rounded-lg p-4 hover:bg-[#F8FCFB] transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start gap-2">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${
                            activity.type === 'success' ? 'bg-green-500' : 
                            activity.type === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
                          }`} />
                          <div>
                            <h3 className="font-medium text-foreground">
                              {activity.message || 'No message'}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'No timestamp'}
                            </p>
                          </div>
                        </div>
                        <Badge variant={activity.type === 'success' ? 'success' : activity.type === 'warning' ? 'warning' : 'destructive'}>
                          {activity.type || 'info'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {(!recentActivity || recentActivity.length === 0) && (
                    <div className="text-center py-8">
                      <Activity className="h-12 w-12 mx-auto mb-2" style={{ color: '#94A3AB' }} />
                      <p className="text-muted-foreground">No recent activity</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
